import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import cn from "classnames";
import { addScriptToBody } from "../../util/dom";
import { setTurnstileCaptchaToken } from "../../redux/actions/authConfig.action";
import { ENV_CONFIG } from "../../config/env";
import { useAppDispatch, useAppSelector } from "@/redux/store";

const turnstileSrc =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export type TurnstileCaptchaRef = {
  reset: () => void;
  setError: (msg: string | null) => void;
  clearError: () => void;
};

type Props = {
  onToken?: (token: string) => void;
  beforeLoading?: () => void;
  onTokenExpired?: () => void;
  afterLoading?: () => void;
  onInterectiveChallangeStarted?: () => void;
  captchaId?: string;
  className?: string;
};

// 1x00000000000000000000AA	Always passes	Visible	Test successful form submissions
// 2x00000000000000000000AB	Always fails	Visible	Test error handling and retry logic
// 1x00000000000000000000BB	Always passes	Invisible	Test invisible widget success flows
// 2x00000000000000000000BB	Always fails	Invisible	Test invisible widget error handling
// 3x00000000000000000000FF	Forces interactive challenge	Visible	Test user interaction scenarios

export const TurnstileCaptcha = forwardRef<TurnstileCaptchaRef, Props>(
  (
    {
      onToken,
      onTokenExpired,
      beforeLoading,
      afterLoading,
      onInterectiveChallangeStarted,
      captchaId = "turnstile_captcha",
      className = "turnstile_container d-flex"
    },
    ref
  ) => {
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const [errorText, setErrorText] = useState<string | null>(null);

    const widgetIdRef = useRef<any>(null);
    const language = useAppSelector(state => state.common.language);
    const dispatch = useAppDispatch();

    const clearToken = () => dispatch(setTurnstileCaptchaToken(""));

    const doReset = () => {
      //@ts-ignore
      const turnstile = window.turnstile;
      if (turnstile && widgetIdRef.current != null) {
        turnstile.reset(widgetIdRef.current);
        console.log(`[Turnstile Captcha] Reset: ${captchaId}`);
      }
      clearToken();
    };

    useImperativeHandle(ref, () => ({
      reset: () => {
        setErrorText(null);
        doReset();
      },
      setError: (msg: string | null) => setErrorText(msg),
      clearError: () => setErrorText(null)
    }));

    // Wait for Turnstile script loaded from index.html (commented out: was addScriptToBody)
    useEffect(() => {
      beforeLoading?.();
      const checkTurnstile = () => {
        //@ts-ignore
        const turnstile = window.turnstile;
        if (turnstile) {
          turnstile.ready(() => setScriptLoaded(true));
          return true;
        }
        return false;
      };
      // Fallback polling for SPA timing: the script tag may exist,
      // but window.turnstile can be available a bit later.
      if (!checkTurnstile()) {
        const id = setInterval(() => {
          if (checkTurnstile()) clearInterval(id);
        }, 100);
        return () => clearInterval(id);
      }
    }, []);

    // Render widget (re-render on language change)
    useEffect(() => {
      //@ts-ignore
      const turnstile = window.turnstile;
      if (!scriptLoaded || !turnstile?.ready) return;

      turnstile.ready(() => {
        if (widgetIdRef.current != null) {
          try {
            turnstile.remove(widgetIdRef.current);
          } catch {}
          widgetIdRef.current = null;
        }

        widgetIdRef.current = turnstile.render(`#${captchaId}`, {
          sitekey: ENV_CONFIG.turnstile_site_key || "0x4AAAAAAAceOLhmQoqDcd1b",

          callback: (token: string) => {
            setErrorText(null);
            dispatch(setTurnstileCaptchaToken(token));
            onToken?.(token);
            console.log(`[Turnstile Captcha] Verified: ${captchaId}`, {
              tokenPreview: token?.slice(0, 20) + "...",
              verifiedAt: new Date().toISOString()
            });
          },

          "before-interactive-callback": () => {
            onInterectiveChallangeStarted?.();
            setErrorText(null);
          },

          "error-callback": error => {
            console.log({ error });
            setErrorText("Verification failed. Please try again.");
            doReset();
            console.warn(`[Turnstile Captcha] Error: ${captchaId}`);
          },

          "timeout-callback": () => {
            setErrorText("The verification timed out. Please try again.");
            onTokenExpired?.();
            doReset();
            console.warn(`[Turnstile Captcha] Timeout: ${captchaId}`);
          },

          "expired-callback": () => {
            setErrorText("The verification expired. Please complete it again.");
            onTokenExpired?.();
            doReset();
            console.warn(`[Turnstile Captcha] Expired: ${captchaId}`);
          },

          size: "normal",
          theme: "light",
          appearance: "interaction-only", // "always" / "execute" / "interaction-only"
          language
        });
        console.log("LOADING FINISHED");
        afterLoading?.();
      });
    }, [scriptLoaded, language, captchaId]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        //@ts-ignore
        const turnstile = window.turnstile;

        if (turnstile && widgetIdRef.current != null) {
          try {
            turnstile.remove(widgetIdRef.current);
          } catch {
            // ignore
          }
        }

        widgetIdRef.current = null;
        clearToken();
      };
    }, []);

    return (
      <div className="turnstile-captcha-wrapper">
        <div id={captchaId} className={cn(className)} />
        {errorText && (
          <span className="error mt-2 text-danger">{errorText}</span>
        )}
      </div>
    );
  }
);

TurnstileCaptcha.displayName = "TurnstileCaptcha";
